import * as PIXI from "pixi.js";
import {PredefinedPIXIAnchorPoint} from "../../PredefinedPIXIAnchorPoint";
import {BlackjackAction} from "../api/BlackjackAction";
import {BlackjackActionName} from "../api/BlackjackActionName";
import data from "../configs/data";
import {Button} from "../controlElements/Button";

export class ActionsButtonsPanel extends PIXI.Container {

    private clearBetsButton: Button;
    private dealButton: Button;
    private splitButton: Button;
    private standButton: Button;
    private insureButton: Button;
    private doNotInsureButton: Button;
    private doubleButton: Button;
    private hitButton: Button;
    private reBetButton: Button;
    private changeBetButton: Button;
    private actionsListeners: Array<Function> = [];

    private readonly BACKGROUND_POSITION = new PIXI.Point(0, 3);
    private readonly BUTTON_LABEL_POSITION = new PIXI.Point(0, 58);

    private readonly BUTTON_LABEL_STYLE = {
        fontSize: 15,
        fontWeight: "bolder",
        fontFamily: "RobotoRegular",
        fill: "#fff",
    };

    private readonly TWO_ACTIONS_VISIBLE = [
        new PIXI.Point(-52, 0),
        new PIXI.Point(52, 0),
    ];
    private readonly THREE_ACTIONS_VISIBLE = [
        new PIXI.Point(-104, 0),
        new PIXI.Point(0, 0),
        new PIXI.Point(104, 0),
    ];
    private readonly FOUR_ACTIONS_VISIBLE = [
        new PIXI.Point(-155, 0),
        new PIXI.Point(-52, 0),
        new PIXI.Point(52, 0),
        new PIXI.Point(155, 0),
    ];

    constructor() {
        super();
        this.createAndAddBackground();
        this.createAndAddInsureButton();
        this.createAndAddSplitButton();
        this.createAndAddDealButton();
        this.createAndAddDoNotInsureButton();
        this.createAndAddDoubleButton();
        this.createAndAddHitButton();
        this.createAndAddClearBetsButton();
        this.createAndAddStandButton();
        this.createAndAddReBetButton();
        this.createAndAddChangeBetButton();
    }

    public addListenerOnActionChosen(listener: Function): void {
        this.actionsListeners.push(listener);
    }

    public addListenerOnClearBetClicked(listener: Function): void {
        this.clearBetsButton.subscribeOnClicked(listener);
    }

    public showAvailableActions(blackjackActionNames: Array<BlackjackActionName>): void {
        this.hideAllButtons();
        for (let i = 0; i < blackjackActionNames.length; i++) {
            if (blackjackActionNames[i] === BlackjackActionName.INSURE) {
                this.insureButton.visible = true;
                this.insureButton.position = this.TWO_ACTIONS_VISIBLE[0];
                this.doNotInsureButton.visible = true;
                this.doNotInsureButton.position = this.TWO_ACTIONS_VISIBLE[1];
            } else if (blackjackActionNames[i] === BlackjackActionName.DEAL) {
                this.dealButton.visible = true;
                this.dealButton.position = this.TWO_ACTIONS_VISIBLE[0];
                this.clearBetsButton.visible = true;
                this.clearBetsButton.position = this.TWO_ACTIONS_VISIBLE[1];
            } else {
                this.showAvailableHandActionButton(blackjackActionNames.length, blackjackActionNames[i], i);
            }
        }
    }

    public showReBetAndChangeBetButtons(): void {
        this.hideAllButtons();
        this.changeBetButton.visible = true;
        this.changeBetButton.position = this.TWO_ACTIONS_VISIBLE[0];
        this.reBetButton.visible = true;
        this.reBetButton.position = this.TWO_ACTIONS_VISIBLE[1];
    }

    public hideAllButtons(): void {
        this.clearBetsButton.visible = false;
        this.splitButton.visible = false;
        this.insureButton.visible = false;
        this.doNotInsureButton.visible = false;
        this.doubleButton.visible = false;
        this.dealButton.visible = false;
        this.hitButton.visible = false;
        this.standButton.visible = false;
        this.changeBetButton.visible = false;
        this.reBetButton.visible = false;
    }

    public enable(): void {
        this.clearBetsButton.enabled = true;
        this.splitButton.enabled = true;
        this.insureButton.enabled = true;
        this.doNotInsureButton.enabled = true;
        this.doubleButton.enabled = true;
        this.dealButton.enabled = true;
        this.hitButton.enabled = true;
        this.standButton.enabled = true;
        this.changeBetButton.enabled = true;
        this.reBetButton.enabled = true;
    }

    public disable(): void {
        this.clearBetsButton.enabled = false;
        this.splitButton.enabled = false;
        this.insureButton.enabled = false;
        this.doNotInsureButton.enabled = false;
        this.doubleButton.enabled = false;
        this.dealButton.enabled = false;
        this.hitButton.enabled = false;
        this.standButton.enabled = false;
        this.changeBetButton.enabled = false;
        this.reBetButton.enabled = false;
    }

    public addOnChangeBetClickedListener(listener: Function): void {
        this.changeBetButton.subscribeOnClicked(listener);
    }

    public addOnReBetClickedListener(listener: Function): void {
        this.reBetButton.subscribeOnClicked(listener);
    }

    private showAvailableHandActionButton(numberOfButtonsToShow: number,
                                          blackjackActionName: BlackjackActionName, buttonIndex: number): void {
        let buttonsPositions;
        if (numberOfButtonsToShow === 2) {
            buttonsPositions = this.TWO_ACTIONS_VISIBLE;
        } else if (numberOfButtonsToShow === 3) {
            buttonsPositions = this.THREE_ACTIONS_VISIBLE;
        } else if (numberOfButtonsToShow === 4) {
            buttonsPositions = this.FOUR_ACTIONS_VISIBLE;
        }

        switch (blackjackActionName) {
            case BlackjackActionName.DOUBLE:
                this.doubleButton.visible = true;
                this.doubleButton.position = buttonsPositions[buttonIndex];
                break;
            case BlackjackActionName.HIT:
                this.hitButton.visible = true;
                this.hitButton.position = buttonsPositions[buttonIndex];
                break;
            case BlackjackActionName.SPLIT:
                this.splitButton.visible = true;
                this.splitButton.position = buttonsPositions[buttonIndex];
                break;
            case BlackjackActionName.STAND:
                this.standButton.visible = true;
                this.standButton.position = buttonsPositions[buttonIndex];
                break;
            default:
                throw new Error("Unhandled param send!");
        }
    }

    private createAndAddDoubleButton(): void {
        this.doubleButton = new Button(data.images.doubleBase, data.images.doubleHover,
            data.images.doubleClick, data.images.doubleInActive,
            data.audios.buttonClicked);
        this.doubleButton.label = this.createButtonLabel(BlackjackActionName.DOUBLE);
        this.doubleButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.DOUBLE));
        });
        this.addChild(this.doubleButton);
    }

    private createAndAddHitButton(): void {
        this.hitButton = new Button(data.images.hitBase, data.images.hitHover,
            data.images.hitClick, data.images.hitInActive,
            data.audios.buttonClicked);
        this.hitButton.label = this.createButtonLabel(BlackjackActionName.HIT);
        this.hitButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.HIT));
        });
        this.addChild(this.hitButton);
    }

    private createButtonLabel(blackjackActionName: string): PIXI.Text {
        const pixiText = new PIXI.Text(blackjackActionName, this.BUTTON_LABEL_STYLE);
        pixiText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        pixiText.position = this.BUTTON_LABEL_POSITION;
        return pixiText;
    }

    private createAndAddReBetButton(): void {
        this.reBetButton = new Button(data.images.reBetBase, data.images.reBetHover,
            data.images.reBetClick, data.images.reBetInActive,
            data.audios.buttonClicked);
        this.reBetButton.label = this.createButtonLabel("REBET");
        this.addChild(this.reBetButton);
    }

    private createAndAddChangeBetButton(): void {
        this.changeBetButton = new Button(data.images.changeBetBase, data.images.changeBetHover,
            data.images.changeBetClick, data.images.changeBetInActive,
            data.audios.buttonClicked);
        this.changeBetButton.label = this.createButtonLabel("CHANGE BET");
        this.addChild(this.changeBetButton);
    }

    private createAndAddSplitButton(): void {
        this.splitButton = new Button(data.images.splitBase, data.images.splitHover,
            data.images.splitClick, data.images.splitInActive,
            data.audios.buttonClicked);
        this.splitButton.label = this.createButtonLabel(BlackjackActionName.SPLIT);
        this.splitButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.SPLIT));
        });
        this.addChild(this.splitButton);
    }

    private createAndAddStandButton(): void {
        this.standButton = new Button(data.images.standBase, data.images.standHover,
            data.images.standClick, data.images.standInActive,
            data.audios.buttonClicked);
        this.standButton.label = this.createButtonLabel(BlackjackActionName.STAND);
        this.standButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.STAND));
        });
        this.addChild(this.standButton);
    }

    private createAndAddDealButton(): void {
        this.dealButton = new Button(data.images.dealButtonBase, data.images.dealButtonOnHover,
            data.images.dealButtonOnClick, data.images.dealButtonInActive,
            data.audios.buttonClicked);
        this.dealButton.label = this.createButtonLabel(BlackjackActionName.DEAL);
        this.dealButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.DEAL));
        });
        this.addChild(this.dealButton);
    }

    private createAndAddClearBetsButton(): void {
        this.clearBetsButton = new Button(data.images.clearBetsBase, data.images.clearBetsHover,
            data.images.clearBetsClick, data.images.clearBetsInActive,
            data.audios.buttonClicked);
        this.clearBetsButton.label = this.createButtonLabel("CLEAR BETS");
        this.addChild(this.clearBetsButton);
    }

    private createAndAddInsureButton(): void {
        this.insureButton = new Button(data.images.insureBase, data.images.insureHover,
            data.images.insureClick, data.images.insureInActive,
            data.audios.buttonClicked);
        this.insureButton.label = this.createButtonLabel("INSURANCE");
        this.insureButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.INSURE, true));
        });
        this.addChild(this.insureButton);
    }

    private createAndAddDoNotInsureButton(): void {
        this.doNotInsureButton = new Button(data.images.notInsureBase, data.images.notInsureHover,
            data.images.notInsureClick, data.images.notInsureInActive,
            data.audios.buttonClicked);
        this.doNotInsureButton.label = this.createButtonLabel("NO");
        this.doNotInsureButton.subscribeOnClicked(() => {
            this.chooseAction(new BlackjackAction(BlackjackActionName.INSURE, false));
        });
        this.addChild(this.doNotInsureButton);
    }

    private chooseAction(blackjackAction: BlackjackAction): void {
        for (let i = 0; i < this.actionsListeners.length; i++) {
            this.actionsListeners[i](blackjackAction);
        }
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.actionsButtonsBackground);
        background.position = this.BACKGROUND_POSITION;
        background.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(background);
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/ActionsButtonsPanel.ts